import { Controller } from "react-hook-form";

import { clsx, Input } from "@alpacahq/ui/old";

import { SelectWithSublabel } from "../../SelectWithSublabel";
import { orderTicketFrameClass, SymbolInfo } from "../shared";
import { ActionButtons } from "../shared/option/ActionButtons";
import { BidMidAsk } from "../shared/option/BidMidAsk";
import { CostSummary } from "../shared/option/CostSummary";
import { OptionLegInfo } from "../shared/option/OptionLegInfo";
import { PositionIntentSelector } from "../shared/option/PositionIntentSelector";
import type { UseOptionControllerReturn } from "./OptionOrderTicket.controller";

interface OptionOrderTicketPresentationProps extends UseOptionControllerReturn {
  onRefreshQuoteClicked: () => void;
  AssetSearch: JSX.Element;
  fill?: boolean;
}

export const OptionOrderTicketPresentation = ({
  onRefreshQuoteClicked,
  AssetSearch,
  fill,

  // Form control & submission
  control,
  onSubmit,
  onCancel,
  onQuantityBlur,
  isSubmitDisabled,
  isSubmitLoading,
  isSubmitCompleted,

  // Position intent
  positionIntent,
  onPositionIntentChange,
  positionIntentOptions,
  isCredit,

  // Underlying asset
  underlying,

  // Visibility flags
  hasQuote,
  hasContract,

  // Quote data
  bid,
  ask,
  midPrice,
  contractPrice,
  quoteLastUpdated,

  // Cost & fees
  estimatedCost,
  buyingPower,
  fees,

  // Order type
  orderType,
  onOrderTypeChange,
  orderTypeOptions,

  // Time in force
  timeInForce,
  onTimeInForceChange,
  timeInForceOptions,

  // Contract details
  strikeAndType,
  expirationDate,
  contractMultiplier,
}: OptionOrderTicketPresentationProps) => (
  <form className={orderTicketFrameClass(fill)} onSubmit={onSubmit}>
    {/* Search */}
    {AssetSearch}

    {/* Underlying Asset Info */}
    <div
      className={clsx(
        "flex max-h-0 flex-col overflow-hidden transition-all duration-300",
        underlying.hasAsset && hasQuote && "max-h-64 gap-2",
      )}
    >
      {underlying.hasAsset && (
        <>
          <p className="m-0 p-0 text-[10px] text-gray-500 leading-[14px]">
            Underlying Asset
          </p>
          <SymbolInfo
            symbol={underlying.symbol}
            name={underlying.name}
            price={underlying.price}
            priceChange={underlying.priceChange}
          />
        </>
      )}
    </div>

    {/* Divider */}
    {hasContract && <hr className="my-0 border-gray-200 border-t" />}

    {/* Option Leg Section */}
    {hasContract && (
      <div className="flex flex-col gap-2">
        <PositionIntentSelector
          value={positionIntent}
          onChange={onPositionIntentChange}
          options={positionIntentOptions}
        />
        <OptionLegInfo
          strikeAndType={strikeAndType}
          expirationDate={expirationDate}
          contractPrice={contractPrice}
        />
      </div>
    )}

    {/* Bid / Mid / Ask */}
    {hasQuote && (
      <BidMidAsk
        bid={bid}
        mid={midPrice.display}
        ask={ask}
        lastUpdated={quoteLastUpdated}
        onRefreshQuoteClicked={onRefreshQuoteClicked}
      />
    )}

    {/* Form Fields */}
    <div className="flex flex-col gap-3">
      {/* Quantity */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="font-normal text-gray-700 text-sm leading-5">
            Quantity
          </span>
          <span className="font-normal text-gray-700 text-xs leading-4">
            x {contractMultiplier} Shares
          </span>
        </div>
        <Controller
          name="quantity"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              onBlur={(e) => {
                field.onBlur();
                onQuantityBlur(e);
              }}
              type="number"
              min="1"
              step="1"
              errorMsg={error?.message}
            />
          )}
        />
      </div>

      {/* Order Type */}
      <SelectWithSublabel
        label="Order Type"
        value={orderType}
        onChange={onOrderTypeChange}
        options={orderTypeOptions}
      />

      {/* Limit Price (limit orders) */}
      {orderType === "limit" && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="font-normal text-gray-700 text-sm leading-5">
              Limit Price
            </span>
            <span className="font-normal text-gray-700 text-xs leading-4">
              {isCredit ? "Credit" : "Debit"}
            </span>
          </div>
          <Controller
            name="limitPrice"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                value={field.value ?? ""}
                type="number"
                min="0"
                step="any"
                leftIcon={<span>$</span>}
                errorMsg={error?.message}
              />
            )}
          />
        </div>
      )}

      {/* Stop Price (stop orders) */}
      {orderType === "stop" && (
        <Controller
          name="stopPrice"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Stop Price"
              {...field}
              value={field.value ?? ""}
              type="number"
              min="0"
              step="any"
              leftIcon={<span>$</span>}
              errorMsg={error?.message}
            />
          )}
        />
      )}

      {/* Stop Price + Limit Price (stop limit orders) */}
      {orderType === "stop_limit" && (
        <div className="grid grid-cols-2 gap-2">
          <Controller
            name="stopPrice"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label="Stop Price"
                {...field}
                value={field.value ?? ""}
                type="number"
                min="0"
                step="any"
                leftIcon={<span>$</span>}
                errorMsg={error?.message}
              />
            )}
          />
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="font-normal text-gray-700 text-sm leading-5">
                Limit Price
              </span>
              <span className="font-normal text-gray-700 text-xs leading-4">
                {isCredit ? "Credit" : "Debit"}
              </span>
            </div>
            <Controller
              name="limitPrice"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  value={field.value ?? ""}
                  type="number"
                  min="0"
                  step="any"
                  leftIcon={<span>$</span>}
                  errorMsg={error?.message}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* Time in Force */}
      <SelectWithSublabel
        label="Time in Force"
        value={timeInForce}
        onChange={onTimeInForceChange}
        options={timeInForceOptions}
      />
    </div>

    {/* Cost Summary */}
    <hr className="my-0 border-gray-200 border-t" />
    <CostSummary
      isCredit={isCredit}
      estimatedCost={estimatedCost}
      buyingPowerDisplay={buyingPower.display}
      buyingPowerLabel={buyingPower.label}
      occClearingFee={fees.occClearingFee}
      optionsRegulatoryFee={fees.optionsRegulatoryFee}
      tafFee={fees.tafFee}
      catFee={fees.catFee}
      showIndicativeEstimate={
        orderType === "stop" || orderType === "stop_limit"
      }
    />

    {/* Action Buttons */}
    <ActionButtons
      onCancel={onCancel}
      isSubmitDisabled={isSubmitDisabled}
      isSubmitLoading={isSubmitLoading}
      isSubmitCompleted={isSubmitCompleted}
    />
  </form>
);
